<template>
  <div class="bdg-table-view">
    <div class="bdg-table">
      <slot name="above-table" />

      <div class="loading-state" v-if="loading">
        <i class="fal fa-sun fa-spin" />
      </div>

      <div
        class="empty-state"
        v-else-if="!filteredItems || !filteredItems.length"
      >
        {{ emptyStateText ? emptyStateText : "No data to view." }}
      </div>

      <template
        v-else-if="$mq !== 'sm' || ($mq === 'sm' && showsTableOnMobile)"
      >
        <table
          class="table pad-first-cell"
          :class="{
            striped: !notStriped,
            'alternate-striped': alternateStriped,
            'is-grouped': rowGroups
          }"
        >
          <tr class="header">
            <th v-if="rowGroups" />
            <th
              class="table-cell"
              :key="`col_${i}`"
              v-for="(col, i) in columns"
            >
              <span v-if="col.label">{{ col.label }}</span>
            </th>
          </tr>

          <tbody class="body">
            <template v-if="rowGroups">
              <template v-for="(rows, groupKey) in rowGroups">
                <tr
                  class="body-row"
                  :class="{ 'first-in-group': index === 0 }"
                  v-for="(row, index) in rows"
                  :key="`row_${groupKey}_${index}`"
                >
                  <td
                    class="grouping-cell"
                    :rowspan="rows.length"
                    v-if="index === 0"
                  >
                    <component
                      :is="getGroupingCellComponentParams(groupKey).component"
                      v-bind.sync="
                        getGroupingCellComponentParams(groupKey).props
                      "
                    />
                  </td>
                  <table-cell
                    :row="row"
                    :col="col"
                    v-for="(col, key) in columns"
                    :key="`${col}-${key}`"
                  />
                </tr>
              </template>
            </template>
            <template v-else>
              <tr
                class="body-row"
                v-for="(row, index) in filteredItems"
                :key="`row-${index}`"
              >
                <table-cell
                  :row="row"
                  :col="col"
                  v-for="(col, key) in columns"
                  :key="`${col}-${key}`"
                />
              </tr>
            </template>
          </tbody>
        </table>
      </template>
      <div
        class="table-footer"
        :class="{
          'mt-4':
            ($mq !== 'sm' || ($mq === 'sm' && showsTableOnMobile)) &&
            (pageOptions.isPaged ||
              ($slots['beside-pagination'] &&
                $slots['beside-pagination'].length))
        }"
      >
        <div
          class="pagination flex items-center justify-start"
          v-if="$mq !== 'sm' || ($mq === 'sm' && showsTableOnMobile)"
        >
          <template v-if="pageOptions.isPaged">
            <button
              class="button tiny"
              @click.prevent="decrementPage()"
              :disabled="isFirstPage"
            >
              <i class="fal fa-chevron-left text-sm" />
            </button>
            <p class="text-sm px-2">
              Page
              <span style="font-variant-numeric: tabular-nums">
                {{ computedPage }}
              </span>
              of
              <span style="font-variant-numeric: tabular-nums">
                {{ totalPages }}
              </span>
            </p>
            <button
              class="button tiny"
              :disabled="isLastPage"
              @click.prevent="incrementPage()"
            >
              <i class="fal fa-chevron-right text-sm" />
            </button>
          </template>
        </div>
        <div class="beside-pagination">
          <slot name="beside-pagination" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DatetimeGroupByOptions,
  TableRowGroupTypes
} from "../../constants/enums";
import TableCell from "./TableCell";
import DayGroupingCell from "./special-cells/grouping-cells/DayGroupingCell";
import StackedTabs from "../StackedTabs";
import HourGroupingCell from "./special-cells/grouping-cells/HourGroupingCell";

export default {
  name: "BdgTableView",
  components: { StackedTabs, TableCell },
  props: {
    rows: { type: Array, default: () => [] },
    columns: { type: Array, required: true },
    striped: { type: Boolean, default: false },
    notStriped: Boolean,
    alternateStriped: Boolean,
    hasSubtabs: { type: Boolean, default: false },
    subtabGroupName: { type: String },
    subtabs: { type: Array, default: () => [] },
    pageOptions: {
      type: Object,
      default: () => {
        return {
          isPaged: false,
          itemsPerPage: 0
        };
      }
    },
    showsTableOnMobile: Boolean,
    areRowsGrouped: Boolean,
    rowsGroupingParams: Object,
    emptyStateText: String,
    loading: Boolean
  },
  created() {
    if (this.pageOptions.isPaged) {
      this.setPaginatorSettings();
      this.currentPage = 1;
    }
  },
  computed: {
    showSubtabs() {
      return this.hasSubtabs && this.subtabs.length > 0;
    },
    filteredItems() {
      if (this.pageOptions.isPaged) {
        const pageStartIndex =
          (this.currentPage - 1) * this.pageOptions.itemsPerPage;
        const pageEndIndex = pageStartIndex + this.pageOptions.itemsPerPage;
        this.setPaginatorSettings();
        return this.rows.slice(pageStartIndex, pageEndIndex);
      }

      return this.rows;
    },
    computedPage() {
      return this.currentPage;
    },
    isFirstPage() {
      return this.currentPage === 1;
    },
    isLastPage() {
      return this.currentPage === this.totalPages;
    },
    rowGroups() {
      if (
        this.areRowsGrouped &&
        this.rowsGroupingParams &&
        this.filteredItems
      ) {
        if (
          this.rowsGroupingParams.groupType &&
          this.rowsGroupingParams.groupType === TableRowGroupTypes.DATETIME
        ) {
          if (
            this.rowsGroupingParams.groupBy &&
            this.rowsGroupingParams.groupBy === DatetimeGroupByOptions.DAY &&
            this.rowsGroupingParams.groupByProperty
          ) {
            const groupByProp = this.rowsGroupingParams.groupByProperty;
            return this.filteredItems.reduce((acc, row) => {
              const day = this.$moment(row[groupByProp]).format("YYYY-MM-DD");
              if (!acc[day]) {
                acc[day] = [];
              }
              acc[day].push(row);

              return acc;
            }, {});
          } else if (
            this.rowsGroupingParams.groupBy &&
            this.rowsGroupingParams.groupBy === DatetimeGroupByOptions.HOUR &&
            this.rowsGroupingParams.groupByProperty
          ) {
            const groupByProp = this.rowsGroupingParams.groupByProperty;
            return this.filteredItems.reduce((acc, row) => {
              const hour =
                this.$moment(row[groupByProp]).format("YYYY-MM-DD HH") + ":00";
              if (!acc[hour]) {
                acc[hour] = [];
              }
              acc[hour].push(row);

              return acc;
            }, {});
          }
        }
      }
      return null;
    }
  },
  methods: {
    incrementPage() {
      if (this.currentPage === this.totalPages) {
        return;
      }

      this.currentPage += 1;
    },
    decrementPage() {
      if (this.currentPage === 1) {
        return;
      }

      this.currentPage -= 1;
    },
    setPaginatorSettings() {
      this.totalPages = Math.ceil(
        this.rows.length / this.pageOptions.itemsPerPage
      );

      if (this.totalPages === 0) {
        this.totalPages = 1;
      }
    },
    getGroupingCellComponentParams(data) {
      if (this.rowGroups) {
        if (
          this.rowsGroupingParams.groupType &&
          this.rowsGroupingParams.groupType === TableRowGroupTypes.DATETIME
        ) {
          if (
            this.rowsGroupingParams.groupBy &&
            this.rowsGroupingParams.groupBy === DatetimeGroupByOptions.DAY
          ) {
            return {
              component: DayGroupingCell,
              props: { date: this.$moment(data).toDate() }
            };
          } else if (
            this.rowsGroupingParams.groupBy &&
            this.rowsGroupingParams.groupBy === DatetimeGroupByOptions.HOUR
          ) {
            return {
              component: HourGroupingCell,
              props: { date: this.$moment(data).toDate() }
            };
          }
        }
      }
      return null;
    }
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 1
    };
  }
};
</script>

<style lang="scss" scoped>
.bdg-table-view {
  .bdg-table {
    .loading-state {
      @apply w-full p-4 text-red text-4xl text-center;
    }

    .table {
      @apply border-collapse w-full;

      &.striped {
        @apply bg-transparent;

        .body {
          .body-row:nth-child(odd) {
            @apply bg-lightgray-lightest;
          }

          .body-row:nth-child(even) {
            @apply bg-lightgray-lighter;
          }

          .body-row.header {
            @apply bg-transparent;
          }
        }
      }

      &.alternate-striped {
        .body-row {
          @apply border border-lightgray;

          &:nth-child(odd) {
            @apply bg-white;
          }

          &:nth-child(even) {
            @apply bg-lightgray-lightest;
          }
        }
      }

      &.pad-first-cell {
        &:not(.is-grouped) {
          .header,
          .body-row {
            .table-cell {
              &:first-child {
                @apply pl-4;
              }
            }
          }
        }
        &.is-grouped {
          .header {
            .table-cell {
              &:nth-child(2) {
                @apply pl-4;
              }
            }
          }

          .body-row {
            &.first-in-group {
              .table-cell {
                &:nth-child(2) {
                  @apply pl-4;
                }
              }
            }
            &:not(.first-in-group) {
              .table-cell {
                &:first-child {
                  @apply pl-4;
                }
              }
            }
          }
        }
      }

      .header,
      .body-row {
        @apply table-row;
        .table-cell {
          @apply pr-4;
        }
      }

      .header {
        @apply text-left;

        .table-cell {
          &:not(:empty) {
            @apply pt-6 pb-2 bg-white text-gray-900 font-bold uppercase text-sm align-bottom;
          }
        }
      }

      .body {
        display: table-row-group;
      }

      .body-row {
        &:not(:first-child) {
          .grouping-cell {
            @apply border-t border-white;
          }
        }

        .grouping-cell {
          @apply w-px border-r border-white p-3 bg-lightgray whitespace-no-wrap;
        }

        .table-cell {
          @apply py-3 align-top;
        }
      }
    }

    .empty-state {
      @apply p-4;
    }
  }

  .table-footer {
    @apply w-full;

    @screen md {
      @apply flex items-center justify-between;
    }

    .beside-pagination {
      &:not(:empty) {
        @apply py-6 px-4;

        @screen md {
          @apply p-0;
        }
      }
    }
  }
}
</style>
